import { useNavigate } from 'react-router-dom';
import { CalendarDays, Link2, PenSquare, Sparkles, Store } from 'lucide-react';

import { Button } from '../../components/core/Button';
import { useTranslation } from '../../context/LanguageContext';
import { getDashboardTabPath } from '../../constants/dashboard';
import type { DashboardTab } from '../../constants/dashboard';
import type { TranslationKey } from '../../i18n';
import type { UserSubscription } from '../../api/subscription.api';
import { formatNumber } from '../../utils/format';

interface StatTileProps {
  labelKey: TranslationKey;
  value: string;
  hint?: string;
}

function StatTile({ labelKey, value, hint }: Readonly<StatTileProps>) {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
        {t(labelKey)}
      </p>
      <p className="text-2xl font-black text-white mt-1.5 leading-none">{value}</p>
      {hint && <p className="text-[11px] text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}

interface NextStepProps {
  icon: React.ComponentType<{ className?: string }>;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
  tab: DashboardTab;
}

function NextStep({ icon: Icon, titleKey, bodyKey, tab }: Readonly<NextStepProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(getDashboardTabPath(tab))}
      className="flex items-start gap-3 text-left w-full rounded-xl border border-slate-700 bg-slate-800 p-4 hover:border-violet-500/50 hover:bg-slate-800/80 transition-colors"
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/15 flex-shrink-0">
        <Icon className="h-4 w-4 text-violet-400" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white">{t(titleKey)}</p>
        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{t(bodyKey)}</p>
      </div>
    </button>
  );
}

export function OverviewPage({
  subscription,
}: Readonly<{ subscription: UserSubscription | null }>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-5 max-w-[1280px]">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm relative overflow-hidden">
          <div className="flex items-start gap-4 relative z-10">
            <div className="flex-1">
              <p className="text-xs font-bold text-violet-400 uppercase tracking-wide mb-1.5">
                {t('common.appName')}
              </p>
              <h2 className="text-2xl font-black text-white leading-snug mb-2">
                {t('dashboard.heroTitle')}
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-5 max-w-sm">
                {t('dashboard.heroBody')}
              </p>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate(getDashboardTabPath('brands'))}
                className="gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t('dashboard.heroCta')}
              </Button>
            </div>
            <div className="hidden sm:flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-700 to-slate-700 border border-slate-600 flex-shrink-0 mt-1 select-none">
              <span className="text-5xl">📣</span>
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-gradient-to-br from-brand-100 to-violet-100 opacity-60 blur-2xl pointer-events-none" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-1 gap-5">
          <StatTile
            labelKey="dashboard.postsThisMonth"
            value={subscription ? formatNumber(subscription.postsPublished) : '—'}
            hint={
              subscription
                ? t('dashboard.ofLimit', { limit: formatNumber(subscription.limits.postsPerMonth) })
                : undefined
            }
          />
          <StatTile
            labelKey="dashboard.generationsThisMonth"
            value={subscription ? formatNumber(subscription.aiGenerations) : '—'}
            hint={
              subscription
                ? t('dashboard.ofLimit', {
                    limit: formatNumber(subscription.limits.aiGenerationsPerMonth),
                  })
                : undefined
            }
          />
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
          {t('dashboard.getStarted')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <NextStep
            icon={Store}
            titleKey="dashboard.stepBrandTitle"
            bodyKey="dashboard.stepBrandBody"
            tab="brands"
          />
          <NextStep
            icon={Link2}
            titleKey="dashboard.stepConnectTitle"
            bodyKey="dashboard.stepConnectBody"
            tab="accounts"
          />
          <NextStep
            icon={PenSquare}
            titleKey="dashboard.stepComposeTitle"
            bodyKey="dashboard.stepComposeBody"
            tab="composer"
          />
          <NextStep
            icon={CalendarDays}
            titleKey="dashboard.stepScheduleTitle"
            bodyKey="dashboard.stepScheduleBody"
            tab="calendar"
          />
        </div>
      </div>
    </div>
  );
}
